import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import {
  ArrowRight, Sparkles, Wifi, Server, ShieldCheck, Cpu,
  Building2, Zap, Database
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

/**
 * Spain / Barcelona — "Creative tech hub" page.
 *
 * Visual identity intentionally different from every other location page:
 * - Editorial split hero, italic display typography (Vogue-style)
 * - Pinterest-style masonry of advantage tiles, varying heights
 * - Manifesto block with surrounding numerical glyphs
 * - Palette: warm terracotta + Mediterranean blue + cream + sun yellow
 *
 * All page text inline as { en, ru }.
 */

const COPY = {
  en: {
    eyebrow: 'Barcelona — where the Mediterranean meets the network',
    h1: 'Spanish VPS,',
    h1Italic: 'from Barcelona',
    h1End: '.',
    sub: 'VPS hosting on the Mediterranean — for everyone tired of pretending the internet starts at Frankfurt.',
    cta: 'Deploy in Barcelona',
    altCta: 'See pricing',
    introLeft: 'Barcelona is one of three cities in the Mediterranean where major submarine cables make landfall. MAREA, 2Africa and the future Medusa cable all surface within an hour of our cabinet, which is plugged into CATNIX, the local internet exchange.',
    introRight: 'For Spanish, Portuguese, Italian, North-African and Latin-American customers, the round-trip from Barcelona is shorter than from any of the usual northern European hubs. For everyone else, it is a beautifully diversified backup region.',
    tilesTitle: 'Why Barcelona, in eight tiles',
    tilesSub: "We didn't pick this city for the tapas (although the tapas help).",
    tiles: [
      { icon: Wifi,        title: 'CATNIX peering',          body: 'Direct exchange with 90+ Spanish networks — single-AS reach to most of Iberia.', span: 'lg:row-span-1' },
      { icon: Building2,   title: 'Submarine cable landing', body: 'MAREA, 2Africa and Medusa all surface within an hour of the cabinet — the West African and Latin American backhaul is unusually short.', span: 'lg:row-span-2' },
      { icon: ShieldCheck, title: 'EU data residency',       body: 'Full GDPR. Spanish AEPD oversight. No third-country transfer surprises.', span: 'lg:row-span-1' },
      { icon: Cpu,         title: 'Modern silicon',          body: 'EPYC-class hosts, NVMe storage, ECC memory across the board.', span: 'lg:row-span-1' },
      { icon: Zap,         title: 'A green grid',            body: 'Catalonia\'s electricity mix is one of the cleanest in southern Europe — your servers spin on more renewable kWh than most "carbon-neutral" badges deliver.', span: 'lg:row-span-2' },
      { icon: Database,    title: 'Daily backups',           body: 'On the Pro plan and up — included, not "available as add-on".', span: 'lg:row-span-1' },
      { icon: Sparkles,    title: '< 4 ms to Madrid',        body: 'A sub-frame round-trip across the country.', span: 'lg:row-span-1' },
      { icon: Server,      title: '99.9% SLA',               body: 'Credits applied automatically against the next invoice — no support ticket required.', span: 'lg:row-span-1' },
    ],
    manifestoTop: 'A node where',
    manifestoBig: 'design and bandwidth',
    manifestoBot: 'belong on the same balcony.',
    manifestoBody: 'Barcelona has hosted half of Europe\'s technology product conferences for a reason — it is one of the few cities that takes both the craft of building and the craft of living seriously. We brought a VPS region here so the latency map of southern Europe finally starts to look like the population map.',
    figs: [
      { big: '< 4 ms',  small: 'to Madrid' },
      { big: '< 14 ms', small: 'to Lisbon' },
      { big: '< 22 ms', small: 'to Casablanca' },
      { big: '< 30 ms', small: 'to Frankfurt' },
    ],
    pricingTitle: 'Spanish VPS plans',
    pricingSub: 'Same plans, same pricing as the rest of our European fleet — Mediterranean latency comes at northern European prices.',
    finalKicker: 'Bring your Spanish VPS where the cables come ashore.',
    finalCta: 'Spin up your Spanish VPS',
  },
  ru: {
    eyebrow: 'Барселона — где Средиземноморье встречает сеть',
    h1: 'Испанский VPS,',
    h1Italic: 'родом из Барселоны',
    h1End: '.',
    sub: 'VPS-хостинг на Средиземноморье — для всех, кто устал делать вид, что интернет начинается во Франкфурте.',
    cta: 'Развернуть в Барселоне',
    altCta: 'К тарифам',
    introLeft: 'Барселона — один из трёх средиземноморских городов, где приземляются крупные подводные кабели. MAREA, 2Africa и будущая Medusa выходят на берег в часе езды от нашей стойки, подключённой к CATNIX — местной точке обмена трафиком.',
    introRight: 'Для клиентов в Испании, Португалии, Италии, Северной Африке и Латинской Америке round-trip из Барселоны короче, чем из любого «обычного» северо-европейского хаба. Для всех остальных — это красиво диверсифицированный резервный регион.',
    tilesTitle: 'Почему Барселона, в восьми плитках',
    tilesSub: 'Мы выбрали этот город не из-за тапаса (хотя тапас, конечно, помогает).',
    tiles: [
      { icon: Wifi,        title: 'Пиринг CATNIX',           body: 'Прямой обмен с 90+ испанскими сетями — однохоповый охват почти всей Иберии.', span: 'lg:row-span-1' },
      { icon: Building2,   title: 'Посадка подводных кабелей', body: 'MAREA, 2Africa и Medusa приземляются в часе езды — backhaul до Западной Африки и Латинской Америки необычно короткий.', span: 'lg:row-span-2' },
      { icon: ShieldCheck, title: 'Резидентство данных в ЕС', body: 'Полноценный GDPR. Надзор испанского AEPD. Никаких сюрпризов с передачей в третьи страны.', span: 'lg:row-span-1' },
      { icon: Cpu,         title: 'Современное железо',       body: 'Хосты класса EPYC, NVMe-хранилище, ECC-память — везде.', span: 'lg:row-span-1' },
      { icon: Zap,         title: 'Зелёная сеть',             body: 'Энергетический микс Каталонии — один из самых чистых в Южной Европе. Ваши серверы крутятся на большем числе возобновляемых кВт·ч, чем у большинства «углеродно-нейтральных» бейджей.', span: 'lg:row-span-2' },
      { icon: Database,    title: 'Ежедневные бэкапы',        body: 'На тарифе Pro и выше — включены, не «доступны как доп».', span: 'lg:row-span-1' },
      { icon: Sparkles,    title: '< 4 мс до Мадрида',        body: 'Round-trip быстрее одного кадра 60 fps.', span: 'lg:row-span-1' },
      { icon: Server,      title: 'SLA 99.9%',                body: 'Компенсации применяются автоматически к следующему счёту — без обращения в поддержку.', span: 'lg:row-span-1' },
    ],
    manifestoTop: 'Узел, где',
    manifestoBig: 'дизайн и пропускная способность',
    manifestoBot: 'наконец стоят на одном балконе.',
    manifestoBody: 'Барселона принимает половину европейских конференций по продуктам неслучайно — это один из редких городов, который всерьёз относится одновременно к ремеслу строить и к ремеслу жить. Мы привезли сюда VPS-регион, чтобы карта задержек Южной Европы наконец-то начала похожа на карту населения.',
    figs: [
      { big: '< 4 мс',  small: 'до Мадрида' },
      { big: '< 14 мс', small: 'до Лиссабона' },
      { big: '< 22 мс', small: 'до Касабланки' },
      { big: '< 30 мс', small: 'до Франкфурта' },
    ],
    pricingTitle: 'Тарифы испанских VPS',
    pricingSub: 'Те же планы и те же цены, что и для остальных европейских локаций — средиземноморская задержка по северо-европейской цене.',
    finalKicker: 'Поставьте свой испанский VPS там, где подводные кабели выходят на берег.',
    finalCta: 'Запустить испанский VPS',
  }
};

export const SpainPage = () => {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;
  const [revealed, setRevealed] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 120);
    return () => clearTimeout(t);
  }, []);

  const scrollPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-[#fefaf3]">
      <SEO page="spain" />
      <Header />

      {/* === HERO — editorial split with italic display typography === */}
      <section className="relative bg-[#fefaf3] overflow-hidden border-b border-[#e5d5b8]/60">
        <div className="absolute -top-12 -right-12 w-[28rem] h-[28rem] rounded-full bg-[#e8754a]/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-24 w-[24rem] h-[24rem] rounded-full bg-[#0e4f8a]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-xs uppercase tracking-[0.3em] text-[#0e4f8a] mb-10 flex items-center gap-3">
            <span className="h-px flex-1 max-w-12 bg-[#0e4f8a]/30" />
            <span>{c.eyebrow}</span>
            <span className="h-px flex-1 max-w-12 bg-[#0e4f8a]/30" />
          </div>

          <h1 className={`font-serif text-[3.5rem] sm:text-[5rem] lg:text-[7rem] leading-[0.95] tracking-tight text-slate-900 transition-all duration-700 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {c.h1}{' '}
            <em className="text-[#e8754a] not-italic font-serif italic">{c.h1Italic}</em>
            <span className="text-[#0e4f8a]">{c.h1End}</span>
          </h1>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="text-2xl sm:text-3xl text-slate-700 font-light leading-snug max-w-2xl">
                {c.sub}
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
              <Button onClick={scrollPricing} className="bg-[#e8754a] hover:bg-[#dd6638] text-white font-bold px-9 py-7 text-lg rounded-md shadow-xl shadow-[#e8754a]/20">
                {c.cta} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <button onClick={scrollPricing} className="text-[#0e4f8a] underline underline-offset-4 decoration-[#e8754a] hover:text-[#e8754a] text-sm">
                {c.altCta}
              </button>
            </div>
          </div>

          <div className="mt-20 grid lg:grid-cols-2 gap-10 max-w-5xl">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed border-l-4 border-[#e8754a]/70 pl-5">{c.introLeft}</p>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed border-l-4 border-[#0e4f8a]/70 pl-5">{c.introRight}</p>
          </div>

          {/* Hero photo mosaic — Sagrada Familia + Mediterranean coast */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:auto-rows-fr">
            <div className="sm:col-span-2 relative rounded-2xl overflow-hidden bg-[#e8754a]/10 aspect-[16/10] shadow-xl">
              <img
                src="/images/locations/barcelona-sagrada.jpg"
                alt="Sagrada Familia — Antoni Gaudi's basilica in Barcelona"
                loading="lazy"
                width="1083" height="1600"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative rounded-2xl overflow-hidden bg-[#0e4f8a]/10 aspect-[16/10] shadow-xl">
              <img
                src="/images/locations/barcelona-aerial.jpg"
                alt="Aerial view of Barcelona, Spain"
                loading="lazy"
                width="1600" height="699"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* === TILES — Pinterest-style masonry, varying heights === */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <h2 className="font-serif text-5xl sm:text-6xl font-black text-slate-900 mb-3 leading-[0.95]">
              {c.tilesTitle}
            </h2>
            <p className="text-lg text-slate-500 italic">{c.tilesSub}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-3 gap-5 lg:auto-rows-[180px]">
            {c.tiles.map((tile, i) => {
              const Icon = tile.icon;
              const isAccent = i === 1 || i === 4; // taller accent tiles get warm bg
              return (
                <div key={i}
                     className={`relative rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1
                       ${tile.span || ''}
                       ${isAccent
                         ? 'bg-gradient-to-br from-[#e8754a] to-[#d35c2e] text-white shadow-xl shadow-[#e8754a]/30'
                         : 'bg-[#fdf6e8] border border-[#ead9b7]/70 text-slate-800 hover:shadow-md'}`}>
                  <Icon className={`w-7 h-7 mb-4 ${isAccent ? 'text-amber-100' : 'text-[#0e4f8a]'}`} />
                  <h3 className={`text-lg font-bold mb-2 leading-tight ${isAccent ? 'text-white' : 'text-slate-900'}`}>{tile.title}</h3>
                  <p className={`text-sm leading-relaxed ${isAccent ? 'text-amber-50/90' : 'text-slate-600'}`}>{tile.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === MANIFESTO — large editorial quote with surrounding numbers === */}
      <section className="py-28 bg-gradient-to-b from-[#fefaf3] to-[#fdedca]/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <p className="text-xl text-slate-500 mb-2">{c.manifestoTop}</p>
              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-black text-slate-900 leading-[0.95] tracking-tight">
                <span className="text-[#0e4f8a]">{c.manifestoBig}</span>
              </h2>
              <p className="text-xl text-slate-500 mt-2 mb-8">{c.manifestoBot}</p>
              <p className="text-lg text-slate-700 leading-relaxed max-w-2xl">{c.manifestoBody}</p>
            </div>

            <div className="lg:col-span-4 grid grid-cols-2 gap-3">
              {c.figs.map((f, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#ead9b7]/80 p-5 text-center shadow-sm">
                  <div className="text-2xl font-black text-[#e8754a]">{f.big}</div>
                  <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{f.small}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <RelatedLocations currentLocation="vps-spain" />

      <div id="pricing">
        <PricingSection hideHeader={false} title={c.pricingTitle} subtitle={c.pricingSub} />
      </div>
      <PaymentMethodsSection transparentBg={false} />

      {/* === FINAL CTA === */}
      <section className="relative py-28 overflow-hidden bg-[#0e4f8a]">
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: 'radial-gradient(circle at 75% 30%, #e8754a 0, transparent 45%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-10 leading-tight">
            {c.finalKicker}
          </h2>
          <Button onClick={scrollPricing} className="bg-[#e8754a] hover:bg-[#dd6638] text-white font-bold px-10 py-7 text-lg rounded-md shadow-2xl">
            {c.finalCta} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-spain" />
      <Footer />
    </div>
  );
};
